import * as React from 'react'
import { CompactLayoutContext } from './CompactLayoutContext'
import { getRemainingVerticalSpace } from '../utils/dom'
import { debounce } from '../utils/fn'

type CompactOnOverflowOptions = {
  /**
   * The buffer space in pixels that should be left before switching to 'spacious' layout.
   * This is useful for preventing flickering when the element is close to overflowing.
   */
  stickinessBuffer?: number
}

/**
 * A hook that observes the height of a DOM element and updates the layout state
 * based on whether the element is overflowing or not.
 */
export const useCompactOnOverflow = (observerId: string, options?: CompactOnOverflowOptions) => {
  const elemRef = React.useRef<HTMLDivElement | null>(null)
  const { requestLayoutState, unregisterLayoutState } = React.useContext(CompactLayoutContext)

  const stickinessBuffer = options?.stickinessBuffer ?? 150

  React.useLayoutEffect(() => {
    const elem = elemRef.current

    if (!elem) return

    // debouncing the function so that we don't perform recalculations when animation is active
    const calculateLayoutState = debounce(() => {
      if (elem.scrollHeight > elem.clientHeight) {
        requestLayoutState(observerId, 'compact')
      } else {
        const remainingSpace = getRemainingVerticalSpace(elem)
        if (remainingSpace > stickinessBuffer) {
          requestLayoutState(observerId, 'spacious')
        }
      }
    }, 50)

    // Initial calculation
    calculateLayoutState()

    // Observe the element and its children for size changes
    const resizeObserver = new ResizeObserver(() => {
      calculateLayoutState()
    })
    resizeObserver.observe(elem)
    elem.childNodes.forEach(child => {
      if (child instanceof HTMLElement) {
        resizeObserver.observe(child)
      }
    })

    // Observe mutations in the element's children
    const mutationObserver = new MutationObserver(() => {
      calculateLayoutState()
    })
    mutationObserver.observe(elem, {
      childList: true,
      subtree: true,
    })

    return () => {
      unregisterLayoutState(observerId)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [requestLayoutState, unregisterLayoutState, observerId, stickinessBuffer])

  return elemRef
}
